import * as React from 'react';
import { Navigate, useLocation, useNavigate, type Location } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertBanner, Button, Card, TextInput } from '@ministry/ui/internal';

import { verifyMfa } from '../lib/auth.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { SESSION_QUERY_KEY } from '../lib/session.js';
import { useT } from '../i18n/strings.js';
import { LocaleToggle } from '../components/LocaleToggle.js';

interface MfaLocationState {
  mfaChallengeId?: string;
  from?: Location;
}

const CODE_LENGTH = 6;

/**
 * SCREEN_SPECS.md I2. Reached only via a `AUTH_MFA_REQUIRED` response from I1 — a direct
 * visit with no challenge id in router state bounces back to login rather than showing a
 * form with nothing to submit against.
 */
export function MfaPage(): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = location.state as MfaLocationState | null;
  const mfaChallengeId = state?.mfaChallengeId;

  const [code, setCode] = React.useState('');

  const mutation = useMutation({
    mutationFn: verifyMfa,
    onSuccess: () => {
      // Invalidate, never seed. `/auth/login` returns a THINNER shape than `/auth/me` —
      // the user id and role codes only — so writing it into the session cache made every
      // consumer read a half-populated session and `/auth/me` was never fetched. The
      // dashboard rendered "Welcome, {name}" with no error anywhere, because a missing
      // field is not a failure in React, it is a blank.
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      navigate('/dashboard', { replace: true });
    },
  });

  const submit = React.useCallback(
    (value: string) => {
      if (!mfaChallengeId || value.length !== CODE_LENGTH || mutation.isPending) return;
      mutation.mutate({ mfaChallengeId, code: value });
    },
    [mfaChallengeId, mutation],
  );

  if (!mfaChallengeId) {
    return <Navigate to="/login" replace />;
  }

  const error = mutation.error;
  let errorBanner: React.ReactNode = null;
  if (error instanceof ApiError && error.code === 'AUTH_MFA_INVALID') {
    errorBanner = <AlertBanner variant="critical" title={t('mfa.error.invalid')} />;
  } else if (error instanceof ApiError && error.code === 'RATE_LIMITED') {
    errorBanner = <AlertBanner variant="warning" title={t('mfa.error.rateLimited')} />;
  } else if (error instanceof NetworkError || (error instanceof ApiError && error.retryable)) {
    errorBanner = (
      <AlertBanner
        variant="critical"
        title={t('mfa.error.network')}
        action={
          <Button size="sm" variant="secondary" onClick={() => submit(code)}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  } else if (error) {
    errorBanner = <AlertBanner variant="critical" title={t('mfa.error.generic')} />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-page p-4">
      {/* The language toggle lives here, not only in AppShell: login and MFA render
          outside the shell, and they are the first screens a staff member sees. */}
      <div className="flex w-full max-w-sm justify-end">
        <LocaleToggle />
      </div>
      <Card className="w-full max-w-sm p-6">
        <h1 className="mb-2 text-xl font-semibold text-text-heading">{t('mfa.title')}</h1>
        <p className="mb-4 text-sm text-text-muted">{t('mfa.instructions')}</p>

        {errorBanner ? <div className="mb-4">{errorBanner}</div> : null}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit(code);
          }}
          className="flex flex-col gap-4"
        >
          <TextInput
            id="mfa-code"
            label={t('mfa.codeLabel')}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            value={code}
            onChange={(event) => {
              const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
              setCode(digitsOnly);
              if (digitsOnly.length === CODE_LENGTH) submit(digitsOnly);
            }}
          />
          <Button type="submit" loading={mutation.isPending} loadingLabel={t('mfa.submitting')}>
            {t('mfa.submit')}
          </Button>
        </form>

        <p className="mt-4 text-sm text-text-muted">{t('mfa.contactAdmin')}</p>
      </Card>
    </div>
  );
}
